import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  getChartTitleTranslation,
  getCurveTermTranslation,
  getUnitTranslation,
  isDateTimeMnemonic,
} from "../../translations/translations";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { useLogsAPI } from "../../hooks/api/use-logsAPI";
import { useWellboreSubscription } from "../../hooks/use-logObserver";
import { useWellboresAPI } from "../../hooks/api/use-wellboresAPI";
import { useStimJobsAPI } from "../../hooks/api/use-stimJobsAPI";
import { useWbGeometryAPI } from "../../hooks/api/use-wbGeometryAPI";
import { useCementJobsAPI } from "../../hooks/api/use-cementJobsAPI";
import {
  useLogChartUnitConversion,
  useStoredUnitSystemId,
  makeCurveKey,
  ExtraCurveRequest,
} from "../../hooks/use-logChartUnitConversion";
import LogChart, { LOG_CHART_COLORS } from "../../components/LogChart/LogChart";
import LogChartDialog from "../../components/LogChart/LogChartDialog";
import LogDataLoader from "../../components/LogChart/LogDataLoader";
import SettingsDialog from "../../components/SettingsDialog/SettingsDialog";
import UserMenu from "../../components/UserMenu/UserMenu";
import WellCementingChart, {
  Fluid as WellCementingFluid,
  Casing as WellCementingCasing,
} from "../../components/WellCementingChart/WellCementingChart";
import PumpSchedule, {
  collectPumpScheduleCurves,
} from "../../components/PumpSchedule/PumpSchedule";
import StepScrollList from "../../components/StepScrollList/StepScrollList";
import RealtimeCard from "../../components/RealtimeCard/RealtimeCard";
import { useWellCementingColors } from "../../styles/wellCementingColors";
import { colorForStageNumber } from "../../utils/stageColors";
import { FluidLogData, Log } from "../../store/types";
import {
  Container,
  NameBlock,
  InnerNameContainer,
  Path,
  ActivePath,
  Spinner,
  EmptyMessage,
  ChartsFlex,
  CalculatedChart,
  WellChart,
  OuterChartsFlex,
  LogCharts,
  PumpScheduleBlock,
  WidgetActionButton,
  ControlsPanel,
  RealtimeCards,
} from "./ViewLog.styles";

const cloneLogChartData = (data: any) => ({
  ...data,
  curveSpecifications: Array.isArray(data?.curveSpecifications)
    ? [...data.curveSpecifications]
    : [],
  data: Array.isArray(data?.data) ? [...data.data] : [],
});

// "calculated" и "fluids" — логи, которые бэкенд каждый раз перезаписывает целиком,
// а не дополняет новыми строками.
const FULL_REPLACE_LOG_NAMES = new Set(["calculated", "fluids"]);
const isFullReplaceLogName = (name?: string) =>
  FULL_REPLACE_LOG_NAMES.has(name?.trim().toLowerCase() ?? "");

// "fluids" отображается только в панели 2D Well Chart (WellCementing) — из общего
// окна просмотра логов (селектор + сетка графиков) его убираем.
const isHiddenFromGeneralView = (name?: string) =>
  name?.trim().toLowerCase() === "fluids";

const isStageCurve = (mnemonic: string) =>
  mnemonic.trim().toLowerCase() === "stagenumber";

const normalizeUnitLabel = (unit?: string) => {
  const normalized = unit?.trim();
  if (!normalized || normalized.toLowerCase() === "unitless") return "";
  return getUnitTranslation(normalized);
};

const toFiniteNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const formatRealtimeValue = (value: unknown, precision?: number) => {
  const number = toFiniteNumber(value);
  if (number === undefined) return value?.toString() ?? "—";

  const options =
    precision === undefined
      ? { maximumFractionDigits: 6 }
      : { minimumFractionDigits: precision, maximumFractionDigits: precision };

  return new Intl.NumberFormat("ru-RU", options).format(number);
};

const getLatestCurveValue = (
  rows: Record<string, unknown>[] | undefined,
  mnemonic: string
) => {
  if (!rows?.length) return undefined;

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const value = rows[index]?.[mnemonic];
    if (value !== null && value !== undefined && value !== "") return value;
  }

  return undefined;
};

const makeLogChartStorageKey = (
  wellUid: string | null | undefined,
  wellboreUid: string | null | undefined,
  logUid: string
) => `${wellUid || ""}:${wellboreUid || ""}:${logUid}`;

const makeLogChartHiddenSeriesStorageKey = (
  storageKey: string,
  title: string
) => `log-chart:hidden-series:${storageKey || title}`;

const readStoredHiddenSeries = (key: string): Record<string, boolean> => {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return {};
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, boolean>)
      : {};
  } catch {
    return {};
  }
};

const areHiddenSeriesEqual = (
  first: Record<string, boolean>,
  second: Record<string, boolean>
) => {
  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);
  if (firstKeys.length !== secondKeys.length) return false;

  return firstKeys.every((key) => first[key] === second[key]);
};

// Кривая, задающая ось глубин 2D-схемы: её единица измерения и точность из активной
// системы единиц подписывают шкалу, к ней же приводятся глубины конструкции скважины.
const WELL_CHART_DEPTH_CURVE = "LeadingEdgeMD";
// Кривая наружного диаметра обсадных колонн (имя совпадает с полем секции): её единица
// и точность из активной системы единиц подписывают диаметры, к ней же приводится odSection.
const WELL_CHART_DIAMETER_CURVE = "odSection";
const WELL_CEMENTING_CHART_UID = "__view-log-well-cementing-chart__";
const PUMP_SCHEDULE_UID = "__view-log-pump-schedule__";

const readStoredStringArray = (key: string) => {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return undefined;
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : undefined;
  } catch {
    return undefined;
  }
};

const ViewLog: React.FC = () => {
  const [searchParams] = useSearchParams();
  const wellUid = searchParams.get("wellUid");
  const wellboreUid = searchParams.get("id");

  const { allLogs, refetchAllLogs, isFetchLoading } = useLogsAPI({
    wellUid: wellUid!,
    wellboreUid: wellboreUid!,
    fetchAll: true,
  });

  const { wellbore } = useWellboresAPI({
    wellUid: wellUid!,
    uid: wellboreUid!,
    fetchAll: false,
  });

  const { stimJob } = useStimJobsAPI({ wellUid, wellboreUid });

  const { geometry: wbGeometry } = useWbGeometryAPI({ wellUid, wellboreUid });

  const { cementJob } = useCementJobsAPI({ wellUid, wellboreUid });

  const cementStages = useMemo(
    () => cementJob?.cementStages ?? [],
    [cementJob]
  );

  const calculatedOverlaySeries = useMemo(() => {
    const intervals = stimJob?.jobIntervals;
    if (!intervals?.length) return [];

    const fracturePoints: { value: number; md: number; tvd?: number }[] = [];
    const reservoirPoints: { value: number; md: number; tvd?: number }[] = [];
    let fractureUnit: string | undefined;
    let reservoirUnit: string | undefined;
    let depthUnit: string | undefined;

    intervals.forEach((interval) => {
      // TODO: switch depth to tvdFormationTop once the calculated log exposes TVD.
      const md = interval.mdFormationTop?.value;
      const tvd = interval.tvdFormationTop?.value;
      if (typeof md !== "number" || !Number.isFinite(md)) return;

      depthUnit = depthUnit || interval.mdFormationTop?.uom;

      const fractureMeasure = interval.reservoirInterval?.fractureDensity;
      const reservoirMeasure = interval.reservoirInterval?.reservoirDensity;
      const fracture = fractureMeasure?.value;
      const reservoir = reservoirMeasure?.value;

      if (typeof fracture === "number" && Number.isFinite(fracture)) {
        fracturePoints.push({ value: fracture, md, tvd });
        fractureUnit = fractureUnit || fractureMeasure?.uom;
      }
      if (typeof reservoir === "number" && Number.isFinite(reservoir)) {
        reservoirPoints.push({ value: reservoir, md, tvd });
        reservoirUnit = reservoirUnit || reservoirMeasure?.uom;
      }
    });

    // Одиночную точку оверлея дублируем на нулевой глубине по стволу, чтобы
    // получить отрезок — иначе линию из одной точки на графике не построить.
    const ensureSegment = (
      points: { value: number; md: number; tvd?: number }[]
    ) => {
      if (points.length === 1) {
        points.push({ ...points[0], md: 0 });
      }
    };
    ensureSegment(fracturePoints);
    ensureSegment(reservoirPoints);

    const byDepth = (a: { md: number }, b: { md: number }) => a.md - b.md;
    fracturePoints.sort(byDepth);
    reservoirPoints.sort(byDepth);

    const series: {
      mnemonic: string;
      unit?: string;
      depthUnit?: string;
      points: { value: number; md: number; tvd?: number }[];
    }[] = [];
    if (fracturePoints.length) {
      series.push({
        mnemonic: "FractureDensity",
        unit: fractureUnit,
        depthUnit,
        points: fracturePoints,
      });
    }
    if (reservoirPoints.length) {
      series.push({
        mnemonic: "ReservoirDensity",
        unit: reservoirUnit,
        depthUnit,
        points: reservoirPoints,
      });
    }
    return series;
  }, [stimJob]);

  const [logsData, setLogsData] = useState<Record<string, any>>({});
  const [loadingErrors, setLoadingErrors] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedLogUids, setSelectedLogUids] = useState<string[]>([]);
  const [openedChartUids, setOpenedChartUids] = useState<string[]>([]);
  const pendingUpdatesRef = useRef<Record<string, any[]>>({});
  const pendingFrameRef = useRef<number | null>(null);
  const replaceLogUidsRef = useRef<Set<string>>(new Set());
  const unitSystemId = useStoredUnitSystemId();
  // Схема цементирования темы не знает — палитру подмешиваем здесь.
  const wellCementingColors = useWellCementingColors();
  const selectedLogsStorageKey = useMemo(
    () => `view-log:selected-logs:${wellUid || ""}:${wellboreUid || ""}`,
    [wellUid, wellboreUid]
  );
  const selectedWidgetsMigrationKey = `${selectedLogsStorageKey}:widgets-v1`;

  useEffect(() => {
    replaceLogUidsRef.current = new Set(
      (allLogs || [])
        .filter((log) => isFullReplaceLogName(log.name))
        .map((log) => log.uid)
    );
  }, [allLogs]);

  const fluidsLog = allLogs?.find(
    (log) => log.name.trim().toLowerCase() === "fluids"
  );
  const hasWellCementingChart = Boolean(fluidsLog);
  const hasPumpSchedule = cementStages.length > 0;

  // Глубины и диаметры конструкции скважины приходят вне логов — регистрируем их
  // единицы, чтобы получить формулы перевода к активной системе единиц.
  const wellGeometryCurves = useMemo<ExtraCurveRequest[]>(() => {
    const depthUnits = new Set<string>();
    const diameterUnits = new Set<string>();
    const addUnit = (target: Set<string>, uom?: string) => {
      const unit = uom?.trim();
      if (unit) target.add(unit);
    };
    addUnit(depthUnits, wbGeometry?.mdBottom?.uom);
    wbGeometry?.wbGeometrySections?.forEach((s) => {
      addUnit(depthUnits, s.mdBottom?.uom);
      addUnit(diameterUnits, s.odSection?.uom);
    });
    return [
      ...Array.from(depthUnits).map((unit) => ({
        curveName: WELL_CHART_DEPTH_CURVE,
        unit,
      })),
      ...Array.from(diameterUnits).map((unit) => ({
        curveName: WELL_CHART_DIAMETER_CURVE,
        unit,
      })),
    ];
  }, [wbGeometry]);

  // Единицы величин расписания закачки регистрируем вместе с геометрией скважины,
  // чтобы для них загрузились те же формулы перевода к активной системе единиц.
  const extraConversionCurves = useMemo<ExtraCurveRequest[]>(
    () => [...wellGeometryCurves, ...collectPumpScheduleCurves(cementStages)],
    [wellGeometryCurves, cementStages]
  );

  const {
    convertedLogsData,
    convertedOverlaySeries,
    curvePrecisions,
    curveUnits,
    conversionRevision,
    convertCurveValue,
  } = useLogChartUnitConversion(
    logsData,
    calculatedOverlaySeries,
    unitSystemId,
    extraConversionCurves
  );

  const wellDepth = convertCurveValue(
    WELL_CHART_DEPTH_CURVE,
    wbGeometry?.mdBottom?.uom,
    wbGeometry?.mdBottom?.value ?? 0
  );

  const casings = useMemo<WellCementingCasing[]>(() => {
    const sections = wbGeometry?.wbGeometrySections;
    if (!sections?.length) return [];
    return sections
      .filter((s) => s.typeHoleCasing?.trim().toLowerCase() === "casing")
      .map((s) => ({
        id: s.uid,
        // Наружный диаметр секции приводим к активной системе единиц (как и глубины).
        diameter: convertCurveValue(
          WELL_CHART_DIAMETER_CURVE,
          s.odSection?.uom,
          s.odSection?.value ?? 0
        ),
        depth: convertCurveValue(
          WELL_CHART_DEPTH_CURVE,
          s.mdBottom?.uom,
          s.mdBottom?.value ?? 0
        ),
      }));
  }, [wbGeometry, convertCurveValue]);

  // Башмак самой глубокой обсадной колонны (по MD) — рисуем его на графике
  // calculated пунктирной константной линией. Глубины уже в активных единицах.
  const deepestCasingDepth = useMemo(() => {
    const depths = casings
      .map((casing) => casing.depth)
      .filter((depth): depth is number => Number.isFinite(depth));
    return depths.length ? Math.max(...depths) : undefined;
  }, [casings]);

  const calculatedDepthReferenceLines = useMemo(
    () => (deepestCasingDepth !== undefined ? [deepestCasingDepth] : []),
    [deepestCasingDepth]
  );

  const fluidsLogData = fluidsLog
    ? (convertedLogsData[fluidsLog.uid] as FluidLogData | undefined)
    : undefined;

  // Подпись шкалы глубин. Единицу берём из спецификации кривой уже сконвертированного
  // лога — она всегда соответствует показанным числам; если лог её не сообщил, остаётся
  // единица измерения активной системы. Точность — оттуда же, из измерения кривой.
  const depthCurveKey = makeCurveKey(WELL_CHART_DEPTH_CURVE);
  const depthUnit =
    fluidsLogData?.curveSpecifications
      ?.find((spec) => makeCurveKey(spec.mnemonic) === depthCurveKey)
      ?.unit?.trim() ||
    curveUnits[depthCurveKey] ||
    "";
  const depthPrecision = curvePrecisions[depthCurveKey];

  // Единица и точность диаметра — из измерения кривой активной системы единиц;
  // при отсутствии перевода остаётся пусто/точность глубины.
  const diameterCurveKey = makeCurveKey(WELL_CHART_DIAMETER_CURVE);
  const diameterUnit = curveUnits[diameterCurveKey] || "";
  const diameterPrecision = curvePrecisions[diameterCurveKey] ?? depthPrecision;

  const { fluids, pipeDepth } = useMemo(() => {
    const rows = fluidsLogData?.data;
    if (!rows?.length) {
      return {
        fluids: [] as WellCementingFluid[],
        pipeDepth: undefined as number | undefined,
      };
    }

    // Труба заканчивается там, где сходятся LeadingEdgeMD затрубной и внутритрубной жидкостей
    // (это и есть башмак трубы/НКТ) — берём максимум по затрубным строкам.
    let maxAnnulusLeadingEdge: number | undefined;
    const mapped: WellCementingFluid[] = rows.map((row) => {
      const isAnnulus = Boolean(row.IsAnnulusPath);
      if (isAnnulus) {
        maxAnnulusLeadingEdge = Math.max(
          maxAnnulusLeadingEdge ?? 0,
          row.LeadingEdgeMD
        );
      }
      return {
        id: row.Index,
        title: row.Label?.trim() || `жидкость`,
        topDepth: Math.min(row.LeadingEdgeMD, row.TrailingEdgeMD),
        bottomDepth: Math.max(row.LeadingEdgeMD, row.TrailingEdgeMD),
        isAnnulus,
        stage: row.StageNumber,
        color: colorForStageNumber(row.StageNumber),
      };
    });

    return { fluids: mapped, pipeDepth: maxAnnulusLeadingEdge };
  }, [fluidsLogData]);

  useEffect(() => {
    if (allLogs && allLogs.length > 0) {
      const specialUids = [
        ...(hasWellCementingChart ? [WELL_CEMENTING_CHART_UID] : []),
        ...(hasPumpSchedule ? [PUMP_SCHEDULE_UID] : []),
      ];
      const allUids = [
        ...allLogs
          .filter((log) => !isHiddenFromGeneralView(log.name))
          .map((log) => log.uid),
        ...specialUids,
      ];
      const storedUids = readStoredStringArray(selectedLogsStorageKey);
      const widgetsMigrated = window.localStorage.getItem(
        selectedWidgetsMigrationKey
      );
      const selectedUids = storedUids
        ? storedUids.filter((uid) => allUids.includes(uid))
        : allUids;

      const nextSelectedUids =
        storedUids && !widgetsMigrated
          ? Array.from(new Set([...selectedUids, ...specialUids]))
          : selectedUids;

      setSelectedLogUids(nextSelectedUids);
      if (storedUids && !widgetsMigrated) {
        window.localStorage.setItem(
          selectedLogsStorageKey,
          JSON.stringify(nextSelectedUids)
        );
      }
      window.localStorage.setItem(selectedWidgetsMigrationKey, "true");
    }
  }, [
    allLogs,
    hasPumpSchedule,
    hasWellCementingChart,
    selectedLogsStorageKey,
    selectedWidgetsMigrationKey,
  ]);

  const handleDataLoaded = useCallback((logUid: string, data: any) => {
    setLogsData((prev) => ({
      ...prev,
      [logUid]: cloneLogChartData(data),
    }));
  }, []);

  const flushPendingUpdates = useCallback(() => {
    pendingFrameRef.current = null;
    const pendingUpdates = pendingUpdatesRef.current;
    pendingUpdatesRef.current = {};

    setLogsData((prev) => {
      let changed = false;
      const next = { ...prev };

      Object.entries(pendingUpdates).forEach(([logUid, batches]) => {
        if (batches.length === 0) return;

        const current = next[logUid];
        const latestBatch = batches[batches.length - 1];

        // Full-replace logs (calculated) push the whole dataset each update, so
        // discard the previous rows and swap in the latest batch wholesale.
        if (replaceLogUidsRef.current.has(logUid)) {
          const points = Array.isArray(latestBatch?.data)
            ? latestBatch.data
            : [];
          if (points.length === 0) return;
          next[logUid] = {
            ...(current || latestBatch),
            curveSpecifications:
              latestBatch?.curveSpecifications ||
              current?.curveSpecifications ||
              [],
            data: [...points],
            __version: ((current as any)?.__version || 0) + 1,
          };
          changed = true;
          return;
        }

        let rows = Array.isArray(current?.data) ? current.data : [];

        if (!Array.isArray(rows) || !Object.isExtensible(rows)) {
          rows = Array.isArray(rows) ? [...rows] : [];
        }

        let appendedCount = 0;
        batches.forEach((batch) => {
          const points = Array.isArray(batch?.data) ? batch.data : [];
          points.forEach((point: any) => {
            rows.push(point);
            appendedCount += 1;
          });
        });

        if (appendedCount === 0) return;

        next[logUid] = {
          ...(current || latestBatch),
          curveSpecifications:
            current?.curveSpecifications ||
            latestBatch?.curveSpecifications ||
            [],
          data: rows,
          __version: ((current as any)?.__version || 0) + 1,
        };
        changed = true;
      });

      return changed ? next : prev;
    });
  }, []);

  const schedulePendingFlush = useCallback(() => {
    if (pendingFrameRef.current !== null) return;
    pendingFrameRef.current = window.requestAnimationFrame(flushPendingUpdates);
  }, [flushPendingUpdates]);

  const handleDataUpdate = useCallback(
    (logUid: string, newData: any) => {
      if (!pendingUpdatesRef.current[logUid]) {
        pendingUpdatesRef.current[logUid] = [];
      }
      pendingUpdatesRef.current[logUid].push(newData);
      schedulePendingFlush();
    },
    [schedulePendingFlush]
  );

  useEffect(
    () => () => {
      if (pendingFrameRef.current !== null) {
        window.cancelAnimationFrame(pendingFrameRef.current);
      }
    },
    []
  );

  const handleError = useCallback((logUid: string) => {
    setLoadingErrors((prev) => ({ ...prev, [logUid]: true }));
  }, []);

  const updateSelectedLogUids = useCallback(
    (uids: string[]) => {
      setSelectedLogUids(uids);
      window.localStorage.setItem(selectedLogsStorageKey, JSON.stringify(uids));
    },
    [selectedLogsStorageKey]
  );

  // На велборе появился лог, которого не было: строк в уведомлении нет — сам список
  // перечитываем REST-запросом, данные подтянет смонтированный LogDataLoader.
  const handleNewLog = useCallback(
    (log: Log) => {
      if (!selectedLogUids.includes(log.uid)) {
        // Выбор графиков восстанавливается из localStorage при каждом обновлении списка,
        // поэтому новый лог сначала попадает в сохранённый выбор, и только потом — refetch.
        updateSelectedLogUids([...selectedLogUids, log.uid]);
      }
      refetchAllLogs();
    },
    [selectedLogUids, updateSelectedLogUids, refetchAllLogs]
  );

  // Пока не подписаны на велбор — хаб не шлёт ни новых строк, ни новых логов.
  useWellboreSubscription(wellUid, wellboreUid, handleNewLog);

  const handleCloseChart = (logUid: string) => {
    setOpenedChartUids((current) => current.filter((uid) => uid !== logUid));
    updateSelectedLogUids(selectedLogUids.filter((uid) => uid !== logUid));
  };

  const handleOpenChart = (logUid: string) => {
    setOpenedChartUids((current) =>
      current.includes(logUid) ? current : [...current, logUid]
    );
  };

  const handleCloseOpenedChart = (logUid: string) => {
    setOpenedChartUids((current) => current.filter((uid) => uid !== logUid));
  };

  const handleCloseWidget = (widgetUid: string) => {
    updateSelectedLogUids(selectedLogUids.filter((uid) => uid !== widgetUid));
  };

  const allLogsLoaded =
    allLogs &&
    allLogs.length > 0 &&
    allLogs.every(
      (log) => logsData[log.uid] !== undefined || loadingErrors[log.uid]
    );

  // Спиннер вместо графиков — только до первой полной загрузки велбора. Дальше появление
  // нового лога подгружает его данные фоном, не перемонтируя уже показанные графики.
  const [chartsReadyKey, setChartsReadyKey] = useState("");
  const wellboreKey = `${wellUid || ""}:${wellboreUid || ""}`;
  const chartsReady = chartsReadyKey === wellboreKey;

  useEffect(() => {
    if (allLogsLoaded) setChartsReadyKey(wellboreKey);
  }, [allLogsLoaded, wellboreKey]);

  const visibleLogs =
    allLogs?.filter((log) => selectedLogUids.includes(log.uid)) || [];
  const showWellCementingChart =
    hasWellCementingChart && selectedLogUids.includes(WELL_CEMENTING_CHART_UID);
  const showPumpSchedule =
    hasPumpSchedule && selectedLogUids.includes(PUMP_SCHEDULE_UID);

  const calculatedLog = allLogs?.find(
    (log) => log.name.toLowerCase() === "calculated"
  );

  const actualLog = useMemo(
    () => allLogs?.find((log) => log.name.toLowerCase() === "actual"),
    [allLogs]
  );
  const openedChartLogs = useMemo(
    () =>
      openedChartUids
        .map((uid) => allLogs?.find((log) => log.uid === uid))
        .filter((log): log is Log => Boolean(log)),
    [allLogs, openedChartUids]
  );
  const actualChartStorageKey = useMemo(
    () =>
      actualLog
        ? makeLogChartStorageKey(wellUid, wellboreUid, actualLog.uid)
        : "",
    [actualLog, wellUid, wellboreUid]
  );
  const actualHiddenSeriesStorageKey = useMemo(
    () =>
      actualLog
        ? makeLogChartHiddenSeriesStorageKey(
            actualChartStorageKey,
            actualLog.name
          )
        : "",
    [actualLog, actualChartStorageKey]
  );
  const [actualHiddenSeries, setActualHiddenSeries] = useState<
    Record<string, boolean>
  >({});
  const [hoveredActualMnemonic, setHoveredActualMnemonic] = useState<
    string | undefined
  >();

  const stepScrollItemsCount = useMemo(
    () =>
      visibleLogs.filter(
        (log) =>
          log.name.trim().toLowerCase() !== "calculated" &&
          !isHiddenFromGeneralView(log.name)
      ).length + (showPumpSchedule ? 1 : 0),
    [visibleLogs, showPumpSchedule]
  );
  const hasStepScrollDots = stepScrollItemsCount > 2;

  useEffect(() => {
    if (!actualHiddenSeriesStorageKey) {
      setActualHiddenSeries((current) =>
        areHiddenSeriesEqual(current, {}) ? current : {}
      );
      return;
    }

    const storedHiddenSeries = readStoredHiddenSeries(
      actualHiddenSeriesStorageKey
    );
    setActualHiddenSeries((current) =>
      areHiddenSeriesEqual(current, storedHiddenSeries)
        ? current
        : storedHiddenSeries
    );
  }, [actualHiddenSeriesStorageKey]);

  useEffect(() => {
    setOpenedChartUids((current) =>
      current.filter(
        (uid) =>
          selectedLogUids.includes(uid) &&
          Boolean(allLogs?.some((log) => log.uid === uid))
      )
    );
  }, [allLogs, selectedLogUids]);

  const handleRealtimeCardToggle = useCallback(
    (mnemonic: string) => {
      setActualHiddenSeries((current) => {
        const next = {
          ...current,
          [mnemonic]: !current[mnemonic],
        };

        if (actualHiddenSeriesStorageKey) {
          window.localStorage.setItem(
            actualHiddenSeriesStorageKey,
            JSON.stringify(next)
          );
        }

        return next;
      });
    },
    [actualHiddenSeriesStorageKey]
  );

  const actualRealtimeCards = useMemo(() => {
    if (!actualLog) return [];

    const actualData = convertedLogsData[actualLog.uid];
    const rows = actualData?.data;
    const axisField = actualLog.indexCurve?.trim().toLowerCase();

    return (
      actualData?.curveSpecifications
        ?.filter((curve) => {
          const mnemonic = curve.mnemonic.trim();
          const lowerMnemonic = mnemonic.toLowerCase();
          return (
            mnemonic &&
            !isDateTimeMnemonic(mnemonic) &&
            !isStageCurve(mnemonic) &&
            lowerMnemonic !== axisField
          );
        })
        .map((curve, index) => ({
          mnemonic: curve.mnemonic,
          name: getCurveTermTranslation(curve.mnemonic),
          units: normalizeUnitLabel(curve.unit),
          color: LOG_CHART_COLORS[index % LOG_CHART_COLORS.length],
          value: formatRealtimeValue(
            getLatestCurveValue(rows, curve.mnemonic),
            curvePrecisions[makeCurveKey(curve.mnemonic)]
          ),
        })) ?? []
    );
  }, [actualLog, convertedLogsData, curvePrecisions]);

  if (isFetchLoading) {
    return (
      <Container>
        <NameBlock>
          <Path>
            <span>{wellbore?.wellName ?? "#"}</span>
            <ActivePath>/ {wellbore?.name ?? "#"}</ActivePath>
          </Path>
          <ControlsPanel>
            <SettingsDialog />
            <UserMenu />
          </ControlsPanel>
        </NameBlock>
        <Spinner>
          <ProgressSpinner />
        </Spinner>
      </Container>
    );
  }

  if (!allLogs || allLogs.length === 0) {
    return (
      <Container>
        <NameBlock>
          <Path>
            <span>{wellbore?.wellName ?? "#"}</span>
            <ActivePath>/ {wellbore?.name ?? "#"}</ActivePath>
          </Path>
          <ControlsPanel>
            <SettingsDialog />
            <UserMenu />
          </ControlsPanel>
        </NameBlock>
        <EmptyMessage>Нет логов для отображения</EmptyMessage>
      </Container>
    );
  }

  const options = [
    ...allLogs
      .filter((log) => !isHiddenFromGeneralView(log.name))
      .map((log) => ({
        label: getChartTitleTranslation(log.name),
        value: log.uid,
      })),
    ...(hasWellCementingChart
      ? [
          {
            label: getChartTitleTranslation("Fluids"),
            value: WELL_CEMENTING_CHART_UID,
          },
        ]
      : []),
    ...(hasPumpSchedule
      ? [
          {
            label: getChartTitleTranslation("PumpSchedule"),
            value: PUMP_SCHEDULE_UID,
          },
        ]
      : []),
  ];

  return (
    <Container>
      <NameBlock>
        <InnerNameContainer>
          <Path>
            <span>{wellbore?.wellName ?? "#"}</span>
            <ActivePath>/ {wellbore?.name ?? "#"}</ActivePath>
          </Path>
        </InnerNameContainer>

        <ControlsPanel>
          <MultiSelect
            value={selectedLogUids}
            options={options}
            onChange={(e) => updateSelectedLogUids(e.value)}
            placeholder="Вид"
            selectedItemsLabel="Вид"
            fixedPlaceholder
            maxSelectedLabels={0}
          />
          <SettingsDialog />
          <UserMenu />
        </ControlsPanel>
      </NameBlock>

      {actualLog &&
        selectedLogUids.includes(actualLog.uid) &&
        actualRealtimeCards.length > 0 && (
          <RealtimeCards>
            {actualRealtimeCards.map((card) => (
              <RealtimeCard
                key={card.mnemonic}
                name={card.name}
                units={card.units}
                color={card.color}
                value={card.value}
                hidden={Boolean(actualHiddenSeries[card.mnemonic])}
                onClick={() => handleRealtimeCardToggle(card.mnemonic)}
                onMouseEnter={() => setHoveredActualMnemonic(card.mnemonic)}
                onMouseLeave={() => setHoveredActualMnemonic(undefined)}
              />
            ))}
          </RealtimeCards>
        )}

      {!chartsReady && (
        <Spinner>
          <ProgressSpinner />
        </Spinner>
      )}

      {allLogs.map((log) => (
        <LogDataLoader
          key={log.uid}
          log={log}
          wellUid={wellUid!}
          wellboreUid={wellboreUid!}
          onDataLoaded={handleDataLoaded}
          onDataUpdate={handleDataUpdate}
          onError={handleError}
        />
      ))}

      {chartsReady && (
        <ChartsFlex>
          {calculatedLog && selectedLogUids.includes(calculatedLog.uid) && (
            <CalculatedChart
              $isFullScale={stepScrollItemsCount > 0 ? false : true}
            >
              <LogChart
                key={calculatedLog.uid}
                data={convertedLogsData[calculatedLog.uid]}
                title={calculatedLog.name}
                indexCurve={calculatedLog.indexCurve}
                overlaySeries={convertedOverlaySeries}
                depthReferenceLines={calculatedDepthReferenceLines}
                curvePrecisions={curvePrecisions}
                autoFitKey={`${unitSystemId}:${conversionRevision}`}
                storageKey={`${wellUid || ""}:${wellboreUid || ""}:${
                  calculatedLog.uid
                }`}
                onClose={() => handleCloseChart(calculatedLog.uid)}
                onOpen={() => handleOpenChart(calculatedLog.uid)}
              />
            </CalculatedChart>
          )}

          <OuterChartsFlex
            $hasStepScrollDots={hasStepScrollDots}
            $isVisible={stepScrollItemsCount > 0 ? true : false}
          >
            <LogCharts $isVisible={stepScrollItemsCount > 0 ? true : false}>
              <StepScrollList visibleItems={stepScrollItemsCount > 1 ? 2 : 1}>
                {chartsReady &&
                  visibleLogs.map((log) =>
                    log.name.trim().toLowerCase() !== "calculated" &&
                    !isHiddenFromGeneralView(log.name) ? (
                      <LogChart
                        key={log.uid}
                        data={convertedLogsData[log.uid]}
                        title={log.name}
                        indexCurve={log.indexCurve}
                        curvePrecisions={curvePrecisions}
                        autoFitKey={`${unitSystemId}:${conversionRevision}`}
                        hiddenSeriesOverride={
                          log.uid === actualLog?.uid
                            ? actualHiddenSeries
                            : undefined
                        }
                        highlightedSeriesMnemonic={
                          log.uid === actualLog?.uid
                            ? hoveredActualMnemonic
                            : undefined
                        }
                        onHiddenSeriesChange={
                          log.uid === actualLog?.uid
                            ? setActualHiddenSeries
                            : undefined
                        }
                        storageKey={makeLogChartStorageKey(
                          wellUid,
                          wellboreUid,
                          log.uid
                        )}
                        onClose={() => handleCloseChart(log.uid)}
                        onOpen={() => handleOpenChart(log.uid)}
                      />
                    ) : undefined
                  )}
                {showPumpSchedule && (
                  <PumpScheduleBlock>
                    <WidgetActionButton
                      onClick={() => handleCloseWidget(PUMP_SCHEDULE_UID)}
                      role="button"
                      aria-label="Закрыть расписание закачки"
                    >
                      <i className="pi pi-times" />
                    </WidgetActionButton>
                    <PumpSchedule
                      stages={cementStages}
                      convertCurveValue={convertCurveValue}
                      curveUnits={curveUnits}
                      curvePrecisions={curvePrecisions}
                    />
                  </PumpScheduleBlock>
                )}
              </StepScrollList>
            </LogCharts>
            {showWellCementingChart ? (
              <WellChart>
                <WidgetActionButton
                  onClick={() => handleCloseWidget(WELL_CEMENTING_CHART_UID)}
                  role="button"
                  aria-label="Закрыть положение жидкостей"
                >
                  <i className="pi pi-times" />
                </WidgetActionButton>
                <WellCementingChart
                  title={getChartTitleTranslation("Fluids")}
                  depth={wellDepth}
                  pipeDepth={pipeDepth}
                  casings={casings}
                  fluids={fluids}
                  colors={wellCementingColors}
                  width="100%"
                  height="100%"
                  layout={{
                    pipeWidth: 30,
                    wellWidth: 80,
                    leftGutter: 200,
                    rightGutter: 200,
                    viewInset: 2,
                  }}
                  grid={{
                    unit: depthUnit,
                    precision: depthPrecision,
                    diameterUnit,
                    diameterPrecision,
                    labelSize: 16,
                    step: wellDepth,
                    casingLabelLayout: "stack",
                  }} // TODO: заменить хардкод ft на ед. измерения
                />
              </WellChart>
            ) : (
              <></>
            )}
          </OuterChartsFlex>
        </ChartsFlex>
      )}

      {openedChartLogs.map((openedChartLog, index) => (
        <LogChartDialog
          key={`dialog-${openedChartLog.uid}`}
          dialogIndex={index}
          data={convertedLogsData[openedChartLog.uid]}
          title={openedChartLog.name}
          indexCurve={openedChartLog.indexCurve}
          overlaySeries={
            openedChartLog.uid === calculatedLog?.uid
              ? convertedOverlaySeries
              : undefined
          }
          depthReferenceLines={
            openedChartLog.uid === calculatedLog?.uid
              ? calculatedDepthReferenceLines
              : undefined
          }
          curvePrecisions={curvePrecisions}
          autoFitKey={`${unitSystemId}:${conversionRevision}`}
          hiddenSeriesOverride={
            openedChartLog.uid === actualLog?.uid
              ? actualHiddenSeries
              : undefined
          }
          highlightedSeriesMnemonic={
            openedChartLog.uid === actualLog?.uid
              ? hoveredActualMnemonic
              : undefined
          }
          onHiddenSeriesChange={
            openedChartLog.uid === actualLog?.uid
              ? setActualHiddenSeries
              : undefined
          }
          storageKey={makeLogChartStorageKey(
            wellUid,
            wellboreUid,
            openedChartLog.uid
          )}
          onClose={() => handleCloseOpenedChart(openedChartLog.uid)}
        />
      ))}
    </Container>
  );
};

export default ViewLog;
